<template>
    <!--
      Segundo nivel de la jerarquía pedido → entradas. La tabla vive dentro del
      desplegable de un pedido, así que va "naked" (sin card propia, ya la aporta
      el contenedor) y con scroll propio: un pedido puede tener cientos de
      entradas y sin `max-body-height` empujaría toda la página.
    -->
    <BaseTable
        data-cy="pedido-entradas-table"
        naked
        dense
        row-key="qr_code"
        :columns="columns"
        :data="entradas"
        max-body-height="360px"
        empty-text="Este pedido no tiene entradas"
    >
        <template #cell-descripcion="{ row }">
            {{ row.concepto }}
        </template>

        <template #cell-datos="{ row }">
            {{ datosDe(row) }}
        </template>

        <template #cell-precio="{ row }">
            {{ formatearPrecio(calcularPrecioEntrada(row)) }}
        </template>

        <template #cell-qr="{ row }">
            <span class="tw:rounded tw:bg-gray-100 tw:px-1.5 tw:py-0.5 tw:font-mono tw:font-bold tw:text-blue-600">
                {{ row.qr_code }}
            </span>
        </template>

        <template #cell-zona="{ row }">
            {{ formatZonaTicket(row) }}
        </template>

        <template #cell-asiento="{ row }">
            {{ formatAsientoTicket(row) }}
        </template>

        <template #cell-estado="{ row }">
            <Pill size="sm" :variant="row.deleted_at ? 'red' : 'emerald'">
                {{ row.deleted_at ? 'Anulada' : 'Activa' }}
            </Pill>
        </template>
    </BaseTable>
</template>

<script setup>
    import { computed } from 'vue';
    import BaseTable from '@/modules/admin/ui/table/BaseTable.vue';
    import Pill from '@/modules/admin/ui/Pill.vue';
    import filters from '@/filters';
    import { formatZonaTicket, formatAsientoTicket } from '@/utils/entradas/formatUbicacionEntrada';
    import { calcularPrecioEntrada } from '@/utils/entradas/precioEntrada';

    const props = defineProps({
        entradas: {
            type: Array,
            default: () => []
        },
        /**
         * Pedido al que pertenecen las entradas. Se usa como respaldo del
         * titular: el backend calcula `entrada.nombre` con `getFullName()` y
         * devuelve '' cuando no hay `user_nombre`, ni abonado, ni usuario con
         * nombre y apellidos; pero el pedido sí puede traer el nombre del
         * comprador (`domicilio_nombre` + `domicilio_apellidos`).
         */
        pedido: {
            type: Object,
            default: null
        }
    });

    const formatearPrecio = value => filters.formatear_precio(value);

    /** Nombre del titular: entrada → usuario de la entrada → comprador del pedido. */
    function datosDe(ticket) {
        const deEntrada = (ticket.nombre || '').trim();
        if (deEntrada) return deEntrada;

        const deUsuario = [ticket.name, ticket.apellidos]
            .filter(Boolean)
            .join(' ')
            .trim();
        if (deUsuario) return deUsuario;

        return [props.pedido && props.pedido.nombre, props.pedido && props.pedido.apellidos]
            .filter(Boolean)
            .join(' ')
            .trim();
    }

    /**
     * La columna de estado sólo tiene sentido si alguna entrada del pedido está
     * anulada: si todas están activas no hay nada que informar y la columna
     * quedaría vacía. Se decide por pedido, no global, porque cada pedido tiene
     * su propio desglose.
     */
    const columns = computed(() => {
        const base = [
            { key: 'descripcion', label: 'Descripción' },
            { key: 'datos', label: 'Datos' },
            { key: 'precio', label: 'Precio + G.G' },
            { key: 'qr', label: 'Código QR' },
            { key: 'zona', label: 'Zona' },
            { key: 'asiento', label: 'Fila / Asiento' }
        ];

        return props.entradas.some(ticket => ticket.deleted_at != null)
            ? [...base, { key: 'estado', label: 'Estado', centered: true }]
            : base;
    });
</script>
